import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Search, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

const Clients = () => {
  const { api } = useAuth();
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState("market_value");
  const [sortOrder, setSortOrder] = useState("desc");
  const [activeTab, setActiveTab] = useState("with_value");
  const [withValueCount, setWithValueCount] = useState(0);
  const [withoutValueCount, setWithoutValueCount] = useState(0);

  const fetchHoldings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: ((page - 1) * pageSize).toString(),
        limit: pageSize.toString(),
        sort_by: sortField,
        sort_order: sortOrder,
      });
      
      if (searchTerm) params.append("search", searchTerm);
      if (activeFilter !== "all") params.append("missing_combo", activeFilter);
      
      // Set filter based on active tab
      if (activeTab === "with_value") {
        params.append("with_value_only", "true");
      } else {
        params.append("without_value_only", "true");
      }
      
      const response = await api.get(`/holdings?${params.toString()}`);
      setHoldings(response.data.holdings || []);
      setTotalCount(response.data.total || 0);
      
      // Update tab counts
      if (response.data.with_value_count !== undefined) {
        setWithValueCount(response.data.with_value_count);
      }
      if (response.data.without_value_count !== undefined) {
        setWithoutValueCount(response.data.without_value_count);
      }
    } catch (error) {
      toast.error("Failed to load holdings");
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, searchTerm, activeFilter, sortField, sortOrder, activeTab]);

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab === "with_value") {
        params.append("with_value_only", "true");
      } else {
        params.append("without_value_only", "true");
      }
      const response = await api.get(`/holdings/stats?${params.toString()}`);
      setStats(response.data);
      
      // Update tab counts
      if (response.data.with_value_total !== undefined) {
        setWithValueCount(response.data.with_value_total);
      }
      if (response.data.without_value_total !== undefined) {
        setWithoutValueCount(response.data.without_value_total);
      }
    } catch (error) {
      console.error("Failed to load stats", error);
    }
  };

  useEffect(() => {
    fetchHoldings();
    fetchStats();
  }, [fetchHoldings, activeTab]);

  const handleSearch = () => {
    setPage(1);
    fetchHoldings();
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortOrder === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-teal-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  const SortableHeader = ({ field, children, className = "" }) => (
    <TableHead 
      className={`text-sm font-sans cursor-pointer hover:bg-gray-50 select-none`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center ${className}`}>
        {children}
        <SortIcon field={field} />
      </div>
    </TableHead>
  );

  const totalPages = Math.ceil(totalCount / pageSize);
  const c = stats.completeness || {};

  // All 16 filters with subtle, muted colors
  const filters = [
    { key: "all", label: "All", count: stats.total, bg: "bg-slate-100 text-slate-700 border-slate-300", activeBg: "bg-slate-200 text-slate-800 border-slate-500" },
    { key: "complete", label: "Complete", count: c.complete, bg: "bg-emerald-50 text-emerald-700 border-emerald-200", activeBg: "bg-emerald-100 text-emerald-800 border-emerald-500", icon: true },
    { key: "pan", label: "PAN", count: c.pan, bg: "bg-amber-50 text-amber-700 border-amber-200", activeBg: "bg-amber-100 text-amber-800 border-amber-400" },
    { key: "mobile", label: "Mobile", count: c.mobile, bg: "bg-orange-50 text-orange-700 border-orange-200", activeBg: "bg-orange-100 text-orange-800 border-orange-400" },
    { key: "email", label: "Email", count: c.email, bg: "bg-violet-50 text-violet-700 border-violet-200", activeBg: "bg-violet-100 text-violet-800 border-violet-400" },
    { key: "pincode", label: "PIN", count: c.pincode, bg: "bg-sky-50 text-sky-700 border-sky-200", activeBg: "bg-sky-100 text-sky-800 border-sky-400" },
    { key: "pan_mobile", label: "PAN+Mob", count: c.pan_mobile, bg: "bg-rose-50 text-rose-700 border-rose-200", activeBg: "bg-rose-100 text-rose-800 border-rose-400" },
    { key: "pan_email", label: "PAN+Email", count: c.pan_email, bg: "bg-pink-50 text-pink-700 border-pink-200", activeBg: "bg-pink-100 text-pink-800 border-pink-400" },
    { key: "pan_pincode", label: "PAN+PIN", count: c.pan_pincode, bg: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", activeBg: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-400" },
    { key: "mobile_email", label: "Mob+Email", count: c.mobile_email, bg: "bg-purple-50 text-purple-700 border-purple-200", activeBg: "bg-purple-100 text-purple-800 border-purple-400" },
    { key: "mobile_pincode", label: "Mob+PIN", count: c.mobile_pincode, bg: "bg-indigo-50 text-indigo-700 border-indigo-200", activeBg: "bg-indigo-100 text-indigo-800 border-indigo-400" },
    { key: "email_pincode", label: "Email+PIN", count: c.email_pincode, bg: "bg-cyan-50 text-cyan-700 border-cyan-200", activeBg: "bg-cyan-100 text-cyan-800 border-cyan-400" },
    { key: "pan_mobile_email", label: "PAN+Mob+Email", count: c.pan_mobile_email, bg: "bg-red-50 text-red-700 border-red-200", activeBg: "bg-red-100 text-red-800 border-red-400" },
    { key: "pan_mobile_pincode", label: "PAN+Mob+PIN", count: c.pan_mobile_pincode, bg: "bg-teal-50 text-teal-700 border-teal-200", activeBg: "bg-teal-100 text-teal-800 border-teal-400" },
    { key: "pan_email_pincode", label: "PAN+Email+PIN", count: c.pan_email_pincode, bg: "bg-lime-50 text-lime-700 border-lime-200", activeBg: "bg-lime-100 text-lime-800 border-lime-400" },
    { key: "mobile_email_pincode", label: "Mob+Email+PIN", count: c.mobile_email_pincode, bg: "bg-yellow-50 text-yellow-700 border-yellow-200", activeBg: "bg-yellow-100 text-yellow-800 border-yellow-400" },
    { key: "pan_mobile_email_pincode", label: "All Missing", count: c.pan_mobile_email_pincode, bg: "bg-gray-100 text-gray-600 border-gray-300", activeBg: "bg-gray-200 text-gray-800 border-gray-500" },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-sans">Holdings</h1>
        </div>
      </div>

      {/* Tabs for With/Without Market Value - FIRST */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); setActiveFilter("all"); }} className="w-full">
        <TabsList className="grid w-96 grid-cols-2">
          <TabsTrigger value="with_value" className="font-sans text-sm">
            With Balance ({withValueCount.toLocaleString()})
          </TabsTrigger>
          <TabsTrigger value="without_value" className="font-sans text-sm">
            Without Value ({withoutValueCount.toLocaleString()})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filter Capsules - Updates based on tab selection, sorted by count (descending) */}
      <div className="flex flex-wrap gap-1">
        {/* Keep "All" and "Complete" at the start, sort the rest by count descending */}
        {[
          ...filters.filter(f => f.key === "all" || f.key === "complete"),
          ...filters.filter(f => f.key !== "all" && f.key !== "complete").sort((a, b) => (b.count || 0) - (a.count || 0))
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => handleFilterClick(f.key)}
            data-testid={`filter-${f.key}`}
            className={`px-2 py-0.5 rounded border text-sm font-medium font-sans transition-all ${
              activeFilter === f.key ? `${f.activeBg} ring-1 ring-offset-1` : `${f.bg} hover:brightness-95`
            }`}
          >
            {f.icon && <CheckCircle className="w-3 h-3 inline mr-0.5" />}
            {f.label} <span className="opacity-70">({f.count?.toLocaleString() || 0})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search PAN, Name, Folio No, Scheme..."
            className="pl-8 h-9 font-sans text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>
        <Button onClick={handleSearch} size="sm" className="font-sans">Search</Button>
      </div>

      {/* Holdings Table */}
      <Card>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Holdings List</CardTitle>
            <Badge variant="outline" className="text-sm">{totalCount.toLocaleString()} records</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
            </div>
          ) : holdings.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              {searchTerm || activeFilter !== "all" ? "No holdings match filters" : "No holdings found."}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader field="pan">PAN</SortableHeader>
                      <SortableHeader field="name">Name</SortableHeader>
                      <SortableHeader field="folio_no">Folio No</SortableHeader>
                      <SortableHeader field="scheme_name">Scheme Name</SortableHeader>
                      <SortableHeader field="isin">ISIN</SortableHeader>
                      <SortableHeader field="market_value" className="justify-end">Market Value</SortableHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {holdings.map((holding, index) => (
                      <TableRow key={`${holding.folio_no}-${index}`} className="text-sm font-sans">
                        <TableCell className="py-2 font-mono text-sm">
                          {holding.pan || <span className="text-red-400">-</span>}
                        </TableCell>
                        <TableCell className="py-2 font-sans max-w-[180px]" title={holding.guardian_name ? `Guardian: ${holding.guardian_name}` : holding.name}>
                          <div className="truncate">
                            {holding.name || "-"}
                          </div>
                          {holding.is_minor && (
                            <span className="text-sm text-purple-600">(Minor)</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 font-mono text-sm">
                          {holding.folio_no || "-"}
                        </TableCell>
                        <TableCell className="py-2 font-sans max-w-[250px]">
                          <div 
                            className="truncate cursor-help" 
                            title={`Product Code: ${holding.scheme_code || '-'}`}
                          >
                            {holding.scheme_name || holding.scheme_code || "-"}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 font-mono text-sm text-gray-600">
                          {holding.isin || <span className="text-gray-300">-</span>}
                        </TableCell>
                        <TableCell 
                          className="py-2 text-right font-sans font-medium text-teal-700 cursor-help"
                          title={`Units: ${holding.units?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) || '0'} | NAV: ₹${holding.nav?.toFixed(2) || '-'}`}
                        >
                          {holding.market_value 
                            ? `₹${holding.market_value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` 
                            : <span className="text-gray-400">-</span>
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-3 text-sm">
                <span className="text-gray-500">
                  {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()}
                </span>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-7" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-2 text-gray-600">{page}/{totalPages || 1}</span>
                  <Button variant="outline" size="sm" className="h-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Clients;
